import { useEffect, useState } from 'react';
import { Box, Button, Chip, Divider, Skeleton, TextField, Typography } from '@mui/material';
import { post } from 'api';
import { download } from 'api';
import dayjs from 'dayjs';
import useNumberFormat from 'hooks/useNumberFormat';
import { enqueueSnackbar } from 'notistack';
import IconifyIcon from 'components/base/IconifyIcon';

// ─── tiny helpers ────────────────────────────────────────────────────────────

const pct = (v) => `${v}%`;

const statusColors = {
  GENERATED: { bg: '#E8F5E9', color: '#2E7D32', label: 'Generated' },
  PENDING: { bg: '#FFF8E1', color: '#F57F17', label: 'Pending' },
  PAID: { bg: '#E3F2FD', color: '#1565C0', label: 'Paid' },
};

// ─── sub-components ──────────────────────────────────────────────────────────

/** A two-column label/value row */
const Row = ({ label, value, bold, accent }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      py: 0.6,
    }}
  >
    <Typography
      variant="body2"
      sx={{ color: 'text.secondary', fontSize: '0.82rem', letterSpacing: 0.2 }}
    >
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        fontWeight: bold ? 700 : 500,
        fontSize: bold ? '0.92rem' : '0.84rem',
        color: accent ? '#1976D2' : 'text.primary',
      }}
    >
      {value}
    </Typography>
  </Box>
);

/** Titled card wrapper */
const Card = ({ title, badge, children, accentColor = '#1976D2' }) => (
  <Box
    sx={{
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 2,
      overflow: 'hidden',
      bgcolor: 'background.paper',
      boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    }}
  >
    {/* card header */}
    <Box
      sx={{
        px: 2,
        py: 1.2,
        background: `linear-gradient(90deg, ${accentColor}18 0%, transparent 100%)`,
        borderBottom: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 700,
          fontSize: '0.78rem',
          letterSpacing: 1,
          textTransform: 'uppercase',
          color: accentColor,
        }}
      >
        {title}
      </Typography>
      {badge}
    </Box>

    {/* card body */}
    <Box sx={{ px: 2, py: 1.4 }}>{children}</Box>
  </Box>
);

/** Stat pill used in attendance */
const StatPill = ({ label, value, color }) => (
  <Box
    sx={{
      flex: '1 1 100px',
      minWidth: 90,
      textAlign: 'center',
      border: '1px solid',
      borderColor: `${color}40`,
      borderRadius: 2,
      py: 1,
      px: 1.5,
      bgcolor: `${color}0D`,
    }}
  >
    <Typography sx={{ fontSize: '1.25rem', fontWeight: 800, color, lineHeight: 1 }}>
      {value}
    </Typography>
    <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary', mt: 0.4, letterSpacing: 0.3 }}>
      {label}
    </Typography>
  </Box>
);

// ─── Loading skeleton ─────────────────────────────────────────────────────────

const LoadingSkeleton = () => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', md: '1fr 1fr', xl: '1fr 1fr 1fr' },
      gap: 2,
      mt: 2,
    }}
  >
    {[...Array(6)].map((_, i) => (
      <Box
        key={i}
        sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}
      >
        <Skeleton variant="rectangular" height={40} />
        <Box sx={{ p: 2 }}>
          {[...Array(4)].map((_, j) => (
            <Skeleton key={j} variant="text" sx={{ mb: 0.8 }} />
          ))}
        </Box>
      </Box>
    ))}
  </Box>
);

// ─── Empty state ──────────────────────────────────────────────────────────────

const EmptyState = ({ month }) => (
  <Box
    sx={{
      mt: 4,
      py: 6,
      textAlign: 'center',
      border: '1px dashed',
      borderColor: 'divider',
      borderRadius: 2,
    }}
  >
    <Typography sx={{ fontSize: '2rem' }}>📭</Typography>
    <Typography variant="subtitle1" sx={{ mt: 1, fontWeight: 600 }}>
      No payroll record found
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
      No salary record generated for {dayjs(month).format('MMMM YYYY')}
    </Typography>
  </Box>
);

// ─── Main component ───────────────────────────────────────────────────────────

const PayrollRecords = ({ state }) => {
  const { currencyFormat } = useNumberFormat();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [value, setValue] = useState(dayjs().format('YYYY-MM'));

  const fetchData = async () => {
    setLoading(true);
    setData(null);
    try {
      const [year, month] = value.split('-').map(Number);
      const res = await post('admin/list_salaryrecord_user', {
        userId: state._id,
        month,
        year,
      });
      if (res.status === 200) {
        setData(res.data);
      } else {
        enqueueSnackbar(res.message || 'Error while fetching salary record', {
          variant: 'error',
          autoHideDuration: 3000,
        });
      }
    } catch (error) {
      enqueueSnackbar(error.message || 'Error while fetching salary record', {
        variant: 'error',
        autoHideDuration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [value]);

  // ─── derived values ──────────────────────────────────────────────────────

  const snap = data?.salarySnapshot ?? {};
  const att = data?.attendanceSnapshot ?? {};
  const policy = data?.policySnapshot ?? {};
  const earnings = data?.earnings ?? {};
  const deductions = data?.deductions ?? {};

  //   const totalEarnings = Object.values(earnings).reduce((s, v) => s + v, 0);
  const totalDeductions =
    (deductions.professionalTax ?? 0) +
    (deductions.pfEmployee ?? 0) +
    (deductions.esicEmployee ?? 0) +
    (deductions.pfEmployer ?? 0) +
    (deductions.esicEmployer ?? 0);

  const statusMeta = statusColors[data?.status] ?? statusColors.GENERATED;

  // attendance bar fill %
  const attendancePct = att.totalWorkingDays
    ? Math.round((att.payableDays / att.totalWorkingDays) * 100)
    : 0;

  // ─── render ──────────────────────────────────────────────────────────────

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const [year, month] = value.split('-').map(Number);
      const res = await download('admin/download_salaryslip', { month, year, userId: state._id });

      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));

      const monthName = new Date(year, month - 1).toLocaleString('default', {
        month: 'short',
      });

      // 🧠 Clean username (avoid spaces issues)
      const userName = `${state.firstName}_${state.lastName}`;

      const fileName = `SalarySlip_${userName}_${monthName}-${year}.pdf`;

      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.click();

      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* ── month picker ── */}
      <Box
        sx={{
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
        }}
      >
        <Box
          sx={{
            py: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <TextField
            type="month"
            variant="filled"
            label="Payroll Month"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            sx={{ width: 220 }}
            slotProps={{
              htmlInput: { min: '2025-11', max: dayjs().format('YYYY-MM') },
            }}
          />

          {data && !loading && (
            <Chip
              label={statusMeta.label}
              size="small"
              sx={{
                bgcolor: statusMeta.bg,
                color: statusMeta.color,
                fontWeight: 700,
                fontSize: '0.75rem',
                borderRadius: 1,
              }}
            />
          )}
        </Box>
        <Button
          variant="soft"
          shape="square"
          color="neutral"
          sx={{ gap: 0.5, p: 1 }}
          loading={downloading}
          onClick={downloadPdf}
        >
          <IconifyIcon icon="material-symbols:add-2-rounded" />
          <Box component="span" sx={{ display: { xs: 'none', sm: 'block' } }}>
            Download
          </Box>
        </Button>
      </Box>

      {/* ── loading ── */}
      {loading && <LoadingSkeleton />}

      {/* ── empty ── */}
      {!loading && !data && <EmptyState month={value} />}

      {/* ── data grid ── */}
      {!loading && data && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr' },
            gap: 2,
            pb: 3,
          }}
        >
          {/* 1 ── Salary Snapshot */}
          <Card title="Salary Snapshot" accentColor="#1976D2">
            <Row label="Monthly CTC" value={currencyFormat(snap.monthlyCTC ?? 0)} bold />
            <Row label="Attendance Required" value={snap.isAttendanceRequired ? 'Yes' : 'No'} />
          </Card>

          {/* 2 ── Attendance */}
          <Card title="Attendance" accentColor="#0288D1">
            {/* mini progress bar */}
            <Box sx={{ mb: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
                <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
                  Payable days
                </Typography>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#0288D1' }}>
                  {att.payableDays} / {att.totalWorkingDays} days ({attendancePct}%)
                </Typography>
              </Box>
              <Box sx={{ height: 6, bgcolor: '#E3F2FD', borderRadius: 3, overflow: 'hidden' }}>
                <Box
                  sx={{
                    height: '100%',
                    width: `${attendancePct}%`,
                    bgcolor: '#0288D1',
                    borderRadius: 3,
                    transition: 'width 0.6s ease',
                  }}
                />
              </Box>
            </Box>

            {/* stat pills */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <StatPill label="Present" value={att.presentDays ?? 0} color="#2E7D32" />
              <StatPill label="Half Days" value={att.halfDays ?? 0} color="#F57F17" />
              <StatPill label="Paid Leave" value={att.paidLeaveDays ?? 0} color="#1976D2" />
              <StatPill label="Unpaid Leave" value={att.unpaidLeaveDays ?? 0} color="#C62828" />
            </Box>
          </Card>

          {/* 3 ── Earnings */}
          <Card title="Earnings" accentColor="#388E3C">
            <Row label="Basic" value={currencyFormat(earnings.basic ?? 0)} />
            <Row label="HRA" value={currencyFormat(earnings.hra ?? 0)} />
            <Row label="Special Allowance" value={currencyFormat(earnings.specialAllowance ?? 0)} />
            <Divider sx={{ my: 1 }} />
            <Row label="Gross Payable" value={currencyFormat(data.grossPayable ?? 0)} bold accent />
          </Card>

          {/* 4 ── Deductions */}
          <Card title="Deductions" accentColor="#D32F2F">
            <Row label="Professional Tax" value={currencyFormat(deductions.professionalTax ?? 0)} />
            <Row label="PF (Employee)" value={currencyFormat(deductions.pfEmployee ?? 0)} />
            <Row label="PF (Employer)" value={currencyFormat(deductions.pfEmployer ?? 0)} />
            {policy.esicEnabled && (
              <Row label="ESIC (Employer)" value={currencyFormat(deductions.esicEmployer ?? 0)} />
            )}
            {policy.esicEnabled && (
              <Row label="ESIC (Employee)" value={currencyFormat(deductions.esicEmployee ?? 0)} />
            )}
            <Divider sx={{ my: 1 }} />
            <Row label="Total Deductions" value={currencyFormat(totalDeductions)} bold />
          </Card>

          {/* 5 ── Employer Contributions */}
          {/* <Card title="Employer Contributions" accentColor="#7B1FA2">
            <Row label="PF (Employer)" value={currencyFormat(deductions.pfEmployer ?? 0)} />
            {policy.esicEnabled && (
              <Row label="ESIC (Employer)" value={currencyFormat(deductions.esicEmployer ?? 0)} />
            )}
          </Card> */}

          {/* 6 ── Policy Config */}
          <Card title="Policy Config" accentColor="#546E7A">
            <Row label="Basic %" value={pct(policy.basicPercent ?? 0)} />
            <Row label="HRA %" value={pct(policy.hraPercent ?? 0)} />
            <Row label="Special Allowance %" value={pct(policy.specialAllowancePercent ?? 0)} />
            <Row label="PF Enabled" value={policy.pfEnabled ? 'Yes' : 'No'} />
            <Row label="ESIC Enabled" value={policy.esicEnabled ? 'Yes' : 'No'} />
            <Row label="PF Employee %" value={pct(policy.pfEmployeePercent ?? 0)} />
            <Row label="PF Employer %" value={pct(policy.pfEmployerPercent ?? 0)} />
            <Row label="PF Max Salary" value={currencyFormat(policy.pfMaxEligibleSalary ?? 0)} />
          </Card>
        </Box>
      )}

      {/* ── Net Payable banner ── */}
      {!loading && data && (
        <Box
          sx={{
            mt: 0.5,
            mb: 3,
            p: 2.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #1976D2 0%, #1565C0 100%)',
            color: '#fff',
            boxShadow: '0 4px 16px rgba(25,118,210,0.3)',
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: '0.75rem',
                opacity: 0.8,
                letterSpacing: 1,
                textTransform: 'uppercase',
              }}
            >
              Net Payable for {dayjs(value).format('MMMM YYYY')}
            </Typography>
            <Typography sx={{ fontSize: '1.75rem', fontWeight: 800, lineHeight: 1.2, mt: 0.3 }}>
              {currencyFormat(data.netPayable ?? 0)}
            </Typography>
          </Box>

          <Box sx={{ textAlign: 'right' }}>
            <Typography sx={{ fontSize: '0.72rem', opacity: 0.75, mb: 0.4 }}>
              Gross ─ Deductions
            </Typography>
            <Typography sx={{ fontSize: '1rem', fontWeight: 600 }}>
              {currencyFormat(data.grossPayable ?? 0)} − {currencyFormat(totalDeductions)}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default PayrollRecords;
